import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateNoteUsingCalendarEventOperation } from "@/store/sync/operations/types";
import {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
} from "@/store/sync/types";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { resolveNoteSourceSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteSourceSyncModelUuid";
import { SourceModelData, SourceUpsertedSyncUpdateValue } from "@/store/sources/types";
import { CreateNoteUsingTemplateOperation } from "@/store/sync/operations/templates/CreateNoteUsingTemplateOperation";
import { notesModule } from "@/modules/notes";
import {
  NoteContentDocumentModelData,
  NoteContentDocumentUpsertedSyncUpdateValue,
  NoteModelData,
  NoteUpsertedSyncUpdateValue,
} from "@/store/note";
import { UNTITLED_CALENDAR_EVENT_TITLE } from "@/domains/untitled/untitled";
import { CreateNoteSourceOperation } from "@/store/sync/operations/sources/CreateNoteSourceOperation";

export class CreateNoteUsingCalendarEventOperation extends BaseSyncOperation<ICreateNoteUsingCalendarEventOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_NOTE_USING_CALENDAR_EVENT";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const syncUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];

    const labels = notesModule.convertEncodedContentToNoteContent(this.payload.encoded_content);
    const primaryLabel = labels.primaryLabel || UNTITLED_CALENDAR_EVENT_TITLE;
    const secondaryLabel = labels.secondaryLabel;
    if (this.payload.template_id) {
      // If template_id is provided, use create note with template
      const createNoteWithTemplateOperation = await new CreateNoteUsingTemplateOperation({
        store: this.store,
        payload: {
          note_id: this.payload.note_id,
          template_id: this.payload.template_id,
          encoded_content: this.payload.encoded_content,
        },
      }).generateOptimisticUpdates();
      syncUpdates.push(...createNoteWithTemplateOperation);
    } else {
      // If template_id is not provided, create note normally
      // 1. Create the note
      const noteValue: NoteUpsertedSyncUpdateValue = {
        model_id: this.payload.note_id,
        model_kind: "NOTE",
        model_version: 0,
        model_data: {
          trashed_at: null,
          created_at: this.committedAt,
          locally_created_at: this.committedAt,
          locally_modified_at: this.committedAt,
          owned_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          created_by_space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          modified_by_space_account_ids: [this.store.spaceAccounts.myPersonalSpaceAccountId],
          primary_label: primaryLabel,
          primary_label_strategy: "INFERRED_FROM_CONTENT",
          secondary_label: secondaryLabel || "",
          secondary_label_strategy: "INFERRED_FROM_CONTENT",
          media_kinds: [],
          mentioned_note_ids: [],
          is_empty: false,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      };
      const noteUpserted: OptimisticSyncUpdate<NoteModelData> = {
        optimistic_update_id: uuidModule.generate(),
        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: noteValue,
      };
      syncUpdates.push(noteUpserted);

      // 2. Create the note content document
      const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
        noteId: this.payload.note_id,
      });
      const noteContentDocumentValue: NoteContentDocumentUpsertedSyncUpdateValue = {
        model_id: noteContentDocumentId,
        model_kind: "NOTE_CONTENT_DOCUMENT",
        model_version: 0,
        model_data: {
          note_id: this.payload.note_id,
          encoded_content: this.payload.encoded_content,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      };
      const noteContentDocumentUpserted: OptimisticSyncUpdate<NoteContentDocumentModelData> = {
        optimistic_update_id: uuidModule.generate(),
        locally_committed_at: this.committedAt,
        kind: "UPSERTED",
        value: noteContentDocumentValue,
      };
      syncUpdates.push(noteContentDocumentUpserted);
    }

    // 3. Create the source sync model
    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    const sourceValue: SourceUpsertedSyncUpdateValue = {
      model_id: sourceId,
      model_kind: "SOURCE",
      model_version: 0,
      model_data: {
        id: sourceId,
        source_kind: "CALENDAR_EVENT",
        associated_artifact_id: this.payload.connected_calendar_event_id,
        primary_label: primaryLabel,
        secondary_label: null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const sourceUpserted: OptimisticSyncUpdate<SourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: sourceValue,
    };
    syncUpdates.push(sourceUpserted);

    // 4. Create the note_source sync model
    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    // Careful here as ensureSourceForRecording might have already created the note source.
    const existingNoteSource = await this.store.noteSources.getAsync(noteSourceId);
    if (!existingNoteSource) {
      const noteSourceOptimisticUpdates = await new CreateNoteSourceOperation({
        store: this.store,
        payload: {
          id: noteSourceId,
          schema_version: 1,
          note_id: this.payload.note_id,
          source_id: sourceId,
        },
      }).generateOptimisticUpdates();
      syncUpdates.push(...noteSourceOptimisticUpdates);
    }

    return syncUpdates;
  }

  async triggerRecompute() {
    if (this.payload.template_id) {
      await new CreateNoteUsingTemplateOperation({
        store: this.store,
        payload: {
          note_id: this.payload.note_id,
          template_id: this.payload.template_id,
          encoded_content: this.payload.encoded_content,
        },
      }).triggerRecompute();
    } else {
      await this.store.notes.recompute(this.payload.note_id);
      const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
        noteId: this.payload.note_id,
      });
      await this.store.noteContentDocuments.recompute(noteContentDocumentId);
    }

    const sourceId = resolveSourceIdForCalendarEvent({
      connectedCalendarEventId: this.payload.connected_calendar_event_id,
    });
    await this.store.sources.recompute(sourceId);

    const noteSourceId = resolveNoteSourceSyncModelUuid({
      noteId: this.payload.note_id,
      sourceId,
    });
    await this.store.noteSources.recompute(noteSourceId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
